import React, { useMemo, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import { agentAuthHeaders } from '../utils/functionAuth';
import { isNativeApp } from '../utils/platform';
import { shareTextFile, shareWasCancelled } from '../utils/nativeShare';
import { formatServes } from '../shared/serviceArea';
import {
  CSV_MAX_BYTES,
  CSV_MAX_ROWS,
  SERVICE_AREA_HINT,
  VENDOR_FIELDS,
  VENDOR_IMPORT_HELP,
  VENDOR_TEMPLATE_FILENAME,
  applyVendorMapping,
  autoMatchVendorHeaders,
  buildVendorPreview,
  buildVendorTemplateCsv,
  parseVendorCsv,
  rowsToCsv,
  vendorMappingMissing,
} from '../shared/vendorCsv';
import { VENDOR_ATTESTATION, VendorAttestationField, postVendorConsent } from './VendorConsent';

async function downloadText(filename, text) {
  try {
    await shareTextFile(filename, text, 'text/csv');
  } catch (error) {
    if (shareWasCancelled(error)) return;
    throw error;
  }
}

function countLabel(count, singular, plural) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function summaryText(totals) {
  const parts = [countLabel(totals.ready, 'ready to add', 'ready to add')];
  if (totals.duplicate) parts.push(countLabel(totals.duplicate, 'already saved', 'already saved'));
  if (totals.error) parts.push(countLabel(totals.error, 'needs a fix', 'need a fix'));
  return parts.join(' · ');
}

function stateFromCsv(text, existingVendors, categories) {
  const parsed = parseVendorCsv(text);
  if (!parsed.ok) return { error: parsed.message };
  if (parsed.rows.length === 0) return { error: 'That file has no data rows.' };
  const mapping = autoMatchVendorHeaders(parsed.headers);
  const base = {
    error: '',
    fileName: 'vendors.csv',
    headers: parsed.headers,
    rawRows: parsed.rows,
    mapping,
  };
  if (vendorMappingMissing(mapping).length > 0) return { ...base, step: 'map' };
  return {
    ...base,
    step: 'preview',
    preview: buildVendorPreview(
      parsed.rows.map((row) => ({ rowNumber: row.rowNumber, values: applyVendorMapping(row.values, mapping) })),
      existingVendors,
      categories,
    ),
  };
}

const VendorImport = ({ profile, categories, existingVendors, initialCsv, onClose, onFinished }) => {
  const { colors } = useTheme();
  const seeded = useMemo(
    () => (initialCsv ? stateFromCsv(initialCsv, existingVendors, categories) : null),
    // Seed once for the screenshot preview. Later edits use the buttons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [step, setStep] = useState(seeded && seeded.step ? seeded.step : 'upload');
  const [fileName, setFileName] = useState(seeded ? seeded.fileName || '' : '');
  const [headers, setHeaders] = useState(seeded ? seeded.headers || [] : []);
  const [rawRows, setRawRows] = useState(seeded ? seeded.rawRows || [] : []);
  const [mapping, setMapping] = useState(seeded ? seeded.mapping || {} : {});
  const [preview, setPreview] = useState(seeded ? seeded.preview || null : null);
  const [visibility, setVisibility] = useState('personal');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(seeded && seeded.error ? seeded.error : '');
  const [addedCount, setAddedCount] = useState(0);

  const missing = useMemo(() => vendorMappingMissing(mapping), [mapping]);
  const readyRows = (preview && preview.rows ? preview.rows : []).filter((row) => row.status === 'ready');

  const readFile = async (file) => {
    setError('');
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Save the spreadsheet as CSV.');
      return;
    }
    if (file.size > CSV_MAX_BYTES) {
      setError('CSV files must be 2 MB or smaller.');
      return;
    }
    const text = typeof file.text === 'function'
      ? await file.text()
      : await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error || new Error('Could not read that file.'));
        reader.readAsText(file);
      });
    const next = stateFromCsv(text, existingVendors, categories);
    if (next.error && !next.step) {
      setError(next.error);
      return;
    }
    setFileName(file.name);
    setHeaders(next.headers || []);
    setRawRows(next.rawRows || []);
    setMapping(next.mapping || {});
    setPreview(next.preview || null);
    setAttested(false);
    setStep(next.step || 'upload');
    if (next.error) setError(next.error);
  };

  const showPreview = () => {
    const rows = rawRows.map((row) => ({
      rowNumber: row.rowNumber,
      values: applyVendorMapping(row.values, mapping),
    }));
    setPreview(buildVendorPreview(rows, existingVendors, categories));
    setStep('preview');
  };

  const runImport = async () => {
    if (!attested) {
      setError('Check the box before adding these vendors.');
      return;
    }
    if (readyRows.length === 0) return;
    setBusy(true);
    setError('');
    let added = 0;
    try {
      for (let i = 0; i < readyRows.length; i += 1) {
        const data = readyRows[i].data;
        const payload = {
          agent_id: profile.id,
          created_by: profile.id,
          name: data.name,
          category: data.category,
          categories: data.categories,
          type: 'maintenance',
          phone: data.phone,
          email: data.email || '',
          website: data.website || '',
          discount: data.discount || '',
          code: data.code || '',
          description: data.description || '',
          visibility,
          service_county_fips: data.service_county_fips,
          service_statewide: data.service_statewide,
        };
        if (visibility === 'team' && profile.team_id) payload.team_id = profile.team_id;
        const { data: saved, error: insertError } = await supabase
          .from('vendors')
          .insert([payload])
          .select('id')
          .single();
        if (insertError) {
          if (insertError.code === '23505') continue;
          throw insertError;
        }
        added += 1;
        await postVendorConsent(await agentAuthHeaders(), {
          action: 'accept',
          consent_type: VENDOR_ATTESTATION,
          vendor_id: saved.id,
        });
      }
      setAddedCount(added);
      setStep('done');
    } catch (err) {
      setAddedCount(added);
      setError(err.message || 'Could not add those vendors.');
    } finally {
      setBusy(false);
    }
  };

  const linkStyle = {
    display: 'inline-block',
    marginTop: '0.35rem',
    padding: '0.7rem 1rem',
    borderRadius: '999px',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontWeight: 650,
    fontSize: '16px',
    textDecoration: 'none',
  };
  const primaryStyle = {
    background: '#0f766e',
    color: '#fff',
    border: 'none',
    borderRadius: '999px',
    padding: '0.7rem 1.1rem',
    fontWeight: 650,
    fontSize: '16px',
    cursor: 'pointer',
  };
  const secondaryStyle = {
    background: 'transparent',
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '999px',
    padding: '0.7rem 1.1rem',
    fontWeight: 650,
    fontSize: '16px',
    cursor: 'pointer',
  };
  const panel = {
    background: colors.surface,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: '1rem',
    padding: '1.25rem',
    width: 'min(640px, 100%)',
    maxHeight: '90vh',
    overflow: 'auto',
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}
      onClick={onClose}
    >
      <div style={panel} onClick={(event) => event.stopPropagation()} role="dialog" aria-labelledby="vendor-import-title">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <h2 id="vendor-import-title" style={{ margin: 0, fontSize: '1.35rem' }}>Import vendors</h2>
          <button type="button" aria-label="Close" onClick={onClose} style={{ background: 'transparent', border: 0, color: colors.textSecondary, fontSize: '1.2rem', cursor: 'pointer' }}>✕</button>
        </div>
        {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

        {step === 'upload' && (
          <div>
            <p style={{ lineHeight: 1.45 }}>{VENDOR_IMPORT_HELP}</p>
            <p style={{ lineHeight: 1.45, color: colors.textSecondary }}>{SERVICE_AREA_HINT} Up to {CSV_MAX_ROWS} rows.</p>
            <a
              href={`/${VENDOR_TEMPLATE_FILENAME}`}
              download={VENDOR_TEMPLATE_FILENAME}
              type="text/csv"
              style={linkStyle}
              onClick={(event) => {
                if (!isNativeApp()) return;
                event.preventDefault();
                downloadText(VENDOR_TEMPLATE_FILENAME, buildVendorTemplateCsv()).catch((downloadError) => {
                  setError(downloadError.message || 'Could not share that file.');
                });
              }}
            >
              Download vendor template
            </a>
            <div
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                readFile(event.dataTransfer.files && event.dataTransfer.files[0]);
              }}
              style={{ marginTop: '1rem', padding: '1.5rem 1rem', border: `2px dashed ${colors.border}`, borderRadius: '0.9rem', textAlign: 'center' }}
            >
              Drop a CSV here
              <div style={{ marginTop: '0.75rem' }}>
                <input aria-label="CSV file" type="file" accept=".csv,text/csv" onChange={(event) => readFile(event.target.files && event.target.files[0])} />
              </div>
            </div>
          </div>
        )}

        {step === 'map' && (
          <div>
            <p>Match the columns from {fileName || 'your file'}. Business name, category, phone, and service area are required.</p>
            {VENDOR_FIELDS.map((field) => (
              <label key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginBottom: '0.75rem' }}>
                <span>{field.label}{field.required ? ' *' : ''}</span>
                <select
                  aria-label={field.label}
                  value={mapping[field.key] || ''}
                  onChange={(event) => setMapping({ ...mapping, [field.key]: event.target.value || null })}
                  style={{ fontSize: '16px', padding: '0.55rem', borderRadius: '0.6rem', border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text }}
                >
                  <option value="">Not in this file</option>
                  {headers.map((header) => <option key={header} value={header}>{header}</option>)}
                </select>
              </label>
            ))}
            {missing.length > 0 ? <p style={{ color: '#b91c1c' }}>Match {missing.join(', ')} before continuing.</p> : null}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button type="button" style={secondaryStyle} onClick={() => setStep('upload')}>Back</button>
              <button type="button" style={primaryStyle} disabled={missing.length > 0} onClick={showPreview}>Check rows</button>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div>
            <p style={{ fontWeight: 650 }}>{summaryText(preview.totals)}</p>
            <p style={{ color: colors.textSecondary, lineHeight: 1.45 }}>Duplicates are skipped. Nothing is added until you say so.</p>
            <div>
              {preview.rows.map((row) => {
                const serves = row.data ? formatServes({
                  service_county_fips: row.data.service_county_fips,
                  service_statewide: row.data.service_statewide,
                }) : '';
                const tone = row.status === 'error' ? '#b91c1c' : colors.textSecondary;
                return (
                  <div key={row.row} style={{ padding: '0.8rem 0', borderTop: `1px solid ${colors.border}` }}>
                    <strong>{row.name || `Row ${row.row}`}</strong>
                    <div style={{ color: colors.textSecondary, marginTop: '0.15rem' }}>
                      {[row.category, row.phone, serves.replace(/^Serves: /, '')].filter(Boolean).join(' · ')}
                    </div>
                    <div style={{ color: tone, marginTop: '0.2rem' }}>
                      {row.status === 'ready' ? 'Ready to add' : row.message}
                    </div>
                  </div>
                );
              })}
            </div>
            {preview.totals.error > 0 ? (
              <button
                type="button"
                style={{ ...secondaryStyle, marginTop: '0.75rem' }}
                onClick={() => {
                  const report = preview.rows.filter((row) => row.status === 'error').map((row) => ({
                    row: row.row,
                    business_name: row.name,
                    message: row.message,
                  }));
                  downloadText('pacarion-vendor-import-errors.csv', rowsToCsv(report)).catch((downloadError) => {
                    setError(downloadError.message || 'Could not share that file.');
                  });
                }}
              >
                Download rows that need a fix
              </button>
            ) : null}
            {profile && profile.team_id ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '1rem' }}>
                Add these to
                <select
                  aria-label="Add these to"
                  value={visibility}
                  onChange={(event) => setVisibility(event.target.value)}
                  style={{ fontSize: '16px', padding: '0.55rem', borderRadius: '0.6rem', border: `1px solid ${colors.border}`, background: colors.surface, color: colors.text }}
                >
                  <option value="personal">Your vendors</option>
                  <option value="team">Team vendors</option>
                </select>
              </label>
            ) : null}
            <VendorAttestationField checked={attested} onChange={setAttested} color={colors.text} />
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button type="button" style={secondaryStyle} onClick={() => setStep(headers.length && missing.length === 0 ? 'upload' : 'map')}>Back</button>
              <button type="button" style={primaryStyle} disabled={busy || !attested || readyRows.length === 0} onClick={runImport}>
                {busy ? 'Adding…' : readyRows.length === 1 ? 'Add 1 vendor' : `Add ${readyRows.length} vendors`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div>
            <p>Added {addedCount} {addedCount === 1 ? 'vendor' : 'vendors'}.</p>
            {preview && preview.totals.duplicate > 0 ? <p>{countLabel(preview.totals.duplicate, 'duplicate was skipped', 'duplicates were skipped')}.</p> : null}
            <button type="button" style={primaryStyle} onClick={() => onFinished && onFinished()}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorImport;
